import { useCallback, useEffect, useMemo, useState } from 'react';
import { identityGateway } from '../../identity/services';
import type { PermissionResponse, RoleResponse } from '../../identity/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** Derives a human-readable module name from a flat `module.action` permission code (e.g.
 * `payrollruns.approve` → "Payroll Runs") — presentation only, grouping the real flat catalog for
 * readability. There is no module/tier concept in the backend data model to read this from (confirmed
 * during EPN-78's precheck); inventing one server-side was explicitly out of scope for this pass. */
function moduleNameFromCode(code: string): string {
  const prefix = code.split('.')[0] ?? code;
  const words = prefix.replace(/([a-z])([A-Z])/g, '$1 $2').split(/(?=[A-Z])|_/).join(' ');
  return words
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

function groupByModule(grants: PermissionResponse[]): [string, PermissionResponse[]][] {
  const groups = new Map<string, PermissionResponse[]>();
  for (const grant of grants) {
    const moduleName = moduleNameFromCode(grant.code);
    const existing = groups.get(moduleName);
    if (existing) existing.push(grant);
    else groups.set(moduleName, [grant]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** Read-only by design — see IRolePermissionService.cs's own doc comment: an endpoint to EDIT role
 * grants is a privilege-escalation surface this build deliberately doesn't ship. Roles/grants come from
 * SeedData/roles-and-permissions.json today. EPN-78 groups the flat permission-code list by module for
 * readability (presentation only — see moduleNameFromCode's own doc comment). */
export function RolesPermissionsPanel() {
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [grants, setGrants] = useState<PermissionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      const roleList = await identityGateway.listRoles();
      setRoles(roleList);
      setSelectedRoleId((current) => current || roleList[0]?.id || '');
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load roles.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (!selectedRoleId) return;
    identityGateway
      .listGrantsForRole(selectedRoleId)
      .then(setGrants)
      .catch((err: unknown) => setError(friendlyErrorMessage(err, 'Could not load permissions.')));
  }, [selectedRoleId]);

  const groupedGrants = useMemo(() => groupByModule(grants), [grants]);

  return (
    <section className="card card--wide" aria-labelledby="roles-permissions-heading">
      <h2 id="roles-permissions-heading">Roles &amp; Permissions</h2>
      {error && <p className="error" role="alert">{error}</p>}
      {loading && <p className="muted" role="status">Loading roles…</p>}

      {!loading && (
        <div className="inline-form">
          <label htmlFor="roles-permissions-role-select">Role</label>
          <select id="roles-permissions-role-select" value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      )}

      {!loading && grants.length === 0 && selectedRoleId && (
        <p className="muted">No permissions granted to this role.</p>
      )}

      {groupedGrants.map(([moduleName, moduleGrants]) => (
        <section key={moduleName} aria-labelledby={`roles-permissions-module-${moduleName}`} style={{ marginTop: 16 }}>
          <h4 id={`roles-permissions-module-${moduleName}`}>{moduleName}</h4>
          <table aria-label={`${moduleName} permissions`}>
            <thead><tr><th>Permission code</th><th>Description</th></tr></thead>
            <tbody>
              {moduleGrants.map((g) => (
                <tr key={g.id}><td>{g.code}</td><td>{g.description}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </section>
  );
}
